import { WindowService } from '../../../common/angular-shared/services/window/window.service';
import { Utils } from '../../../common/js/Utils';
import { SubjectManager } from '../../../common/js/subjectManager/SubjectManager';
import { Subject } from 'rxjs';
import { Observable } from 'rxjs';
import { CoverpathControllerInterface } from '../interfaces/controller/CoverpathControllerInterface';
import { Diagnosis } from '../../../common/js/customer/Diagnosis';
import { DiagnosisShort } from '../../../common/js/customer/DiagnosisShort';
import _ from 'lodash';
import { ValidationService } from '../services/validation/validation.service';
import { ApplicationSectionsList, ApplicationSectionUtils } from './ApplicationSection';
import { ValidationUtils } from '../../../coverpath-common/js/static/ValidationUtils';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';
export interface ValueDescription {
  value: string | boolean | number;
  description: string;
}
export class CasMetadataNode {
  id: string;
  prop: string;
  requiredProps: Array<string>;
  requiredBasedOn: Array<string>;
  windowService: WindowService;
  controller: CoverpathControllerInterface;
  children: Array<CasMetadataNode> = [];
  section: string;
  controlType: string;
  qText: string;
  uiLevel: number;
  level: number = 1;
  fid: string;
  addFuncSuffix: boolean; // applies to iterate control and indicates how to add a new item to the collection
  removeFuncSuffix: boolean; // applies to iterate control and indicates how to remove a item to the collection
  pronounLocked: boolean;
  parentLevel1NodeId: string;
  topLevel: boolean;
  placeholder: string;
  responsesAllowed: Array<ValueDescription> | null;
  featureFlags: Array<{ [key: string]: string }> | null;
  subRequiredIf: string;
  customProps: Array<string>;
  subProperty: string;
  validation: string;
  parent: CasMetadataNode;
  showifExp: string;
  subscribeToProps: Array<string> = [];
  type: string;
  propertyChangedSubject: Subject<void>;
  propertyChangedObservable$: Observable<void>;
  interestedIn: Array<string> = [];
  subscriptionsInFlight: Array<string> = [];
  arrayType: string;
  helpText: string;
  needsFixed: boolean;
  inNestedEntity: boolean;
  entity: string;
  subtext: string;
  styleClass: string;
  sectionValidation?: boolean;
  permanentlyDisabledConditional: Array<string> = [];
  iterateLinkLabel: string;
  questionDescription: string;
  optionalInputControlType: string;
  optionalInputProperty: string;
  optionalInputPropertyType: string;
  optionalInputLabel: string;
  obfuscateValueConditional?: Array<string> = [];
  readonlyReason?: string;
  readonlyConditional?: Array<string> = [];
  modalType: string;
  dataReliesOn?: Array<string> = [];
  /** When using nodes for section progress and verification, we need to keep track of where they are in iterate-collections.
   * @remarks the 'true' node that iterate-collect components are pointing to will always hold the maximum index used
   */
  loopIndex?: number;
  conditionalDestroyBlock: boolean;
  tooltipDisabledOverride?: boolean;
  customClientValidations: Array<string>;
  isTeleCMIQuestion: boolean;
  isQuestionLockable: boolean;

  get optional(): boolean {
    if (this.isOptionalDueToTeleCMI() || this.isOptionalDueToApplicationData()) {
      return true;
    }

    const optsRequiredStr = this.requiredProps && this.requiredProps[0];
    return optsRequiredStr ? optsRequiredStr.toLowerCase() === 'no' : false;
  }

  constructor(controller: CoverpathControllerInterface, prop?, options?) {
    this.controller = controller;
    this.propertyChangedSubject = new Subject<void>();
    this.propertyChangedObservable$ = this.propertyChangedSubject.asObservable();

    this.prop = prop;
    const opts = options || {};

    if (prop) {
      const tokens = prop.split('.');
      tokens.pop();
      this.entity = tokens.join('.');
    }

    this.inNestedEntity = !!opts.inNestedEntity;

    this.id = opts.id;
    this.section = opts.section;
    this.controlType = opts.controlType;
    this.fid = opts.fid ? opts.fid : this.buildFid(this.prop);
    this.pronounLocked = opts.pronounLocked ? opts.pronounLocked.toLowerCase() === 'true' : false;
    this.qText = this.id !== '0001' ? this.substitutePronouns(opts.qText) : opts.qText;
    this.customProps = opts.customProps ? opts.customProps.split(',') : null;
    this.subProperty = opts.subProperty;
    this.placeholder = opts.placeholder;
    this.customProps = opts.customProps ? opts.customProps.split(',') : null;
    this.validation = opts.validation;
    this.requiredProps = opts.required ? opts.required.split(',') : null;
    this.requiredBasedOn = opts.requiredBasedOn ? opts.requiredBasedOn.split(',') : null;
    this.subRequiredIf = opts.subRequiredIf;
    this.styleClass = opts.styleClass;

    this.topLevel = !!this.section;
    if (!this.topLevel) {
      this.level = Number(opts.level);
      this.uiLevel = opts.uiLevel >= 0 ? Number(opts.uiLevel) : this.level - 1;
    }
    this.responsesAllowed = this.buildResponsesAllowed(opts.responsesAllowed);
    this.showifExp = opts.showifExp;
    this.subscribeToProps = opts.subscribeToProps || [];
    this.type = opts.type;
    this.interestedIn = opts.interestedIn ? opts.interestedIn.split(',') : [];
    if (this.type) {
      this.arrayType = this.type.match(/array *of *([A-Za-z]*)/) ? RegExp.$1 : '';
    }
    this.customProps = opts.customProps ? opts.customProps.split(',') : null;
    this.helpText = opts.helpText ? opts.helpText : null;
    this.featureFlags = opts.featureFlags ? JSON.parse(opts.featureFlags.replace(/\'/g, '"')) : null;
    this.addFuncSuffix = !!opts.addFuncSuffix;
    this.removeFuncSuffix = !!opts.removeFuncSuffix;
    this.permanentlyDisabledConditional = opts.permanentlyDisabledConditional
      ? opts.permanentlyDisabledConditional.split(',')
      : [];
    this.questionDescription = opts.questionDescription;
    this.optionalInputControlType = opts.optionalInputControlType;
    this.optionalInputProperty = opts.optionalInputProperty;
    this.optionalInputPropertyType = opts.optionalInputPropertyType;
    this.optionalInputLabel = opts.optionalInputLabel;
    this.setupEventSubscriptions();
    this.subtext = opts.subtext;
    this.modalType = opts.modalType;
    this.iterateLinkLabel = opts.iterateLinkLabel;
    if (this.section === 'Existing Policies' && !this.controller.customer.isCoverpath2019MetadataEnabled()) {
      this.sectionValidation = true;
    }
    this.obfuscateValueConditional = opts.obfuscateValueConditional ? opts.obfuscateValueConditional.split(',') : [];
    this.readonlyConditional = opts.readonlyConditional ? opts.readonlyConditional.split(',') : [];
    this.readonlyReason = opts.readonlyReason;
    this.dataReliesOn = opts.dataReliesOn ? opts.dataReliesOn.split(',') : [];
    this.conditionalDestroyBlock = opts.conditionalDestroyBlock
      ? opts.conditionalDestroyBlock.toLowerCase() === 'true'
      : false;

    this.tooltipDisabledOverride = opts.tooltipDisabledOverride
      ? opts.tooltipDisabledOverride.toLowerCase() === 'true'
      : null;

    this.customClientValidations = opts.customClientValidations ? opts.customClientValidations.split(',') : null;

    this.isTeleCMIQuestion = opts.isTeleCMIQuestion ? opts.isTeleCMIQuestion.toLowerCase() === 'true' : false;
    this.isQuestionLockable = opts.isQuestionLockable ? opts.isQuestionLockable.toLowerCase() === 'true' : false;
  }

  addChild(node, parentId?) {
    this.children.push(node);

    if (this.section === 'Existing Policies' && !this.controller.customer.isCoverpath2019MetadataEnabled()) {
      node.sectionValidation = true;
    }

    node.parent = this;
    node.parentLevel1NodeId = parentId;

    return node;
  }

  resetLevel() {
    // For nodes in an iterate control, the level needs to be reset
    // as special handling in UI is done for the iterate tag
    this.level = 2;
    this.uiLevel = 0;
  }

  shallowCopy(): CasMetadataNode {
    const newNode = new CasMetadataNode(this.controller, this.prop);

    newNode.entity = this.entity;
    newNode.inNestedEntity = this.inNestedEntity;
    newNode.controlType = this.controlType;
    newNode.customProps = this.customProps;
    newNode.requiredProps = this.requiredProps;
    newNode.section = this.section;
    newNode.children = this.children;
    newNode.qText = this.qText;
    newNode.showifExp = this.showifExp;
    newNode.type = this.type;
    newNode.topLevel = this.topLevel;
    newNode.interestedIn = this.interestedIn;
    newNode.arrayType = this.arrayType;
    newNode.helpText = this.helpText;
    newNode.subtext = this.subtext;
    newNode.featureFlags = this.featureFlags;
    newNode.addFuncSuffix = this.addFuncSuffix;
    newNode.removeFuncSuffix = this.removeFuncSuffix;
    newNode.permanentlyDisabledConditional = this.permanentlyDisabledConditional;
    newNode.iterateLinkLabel = this.iterateLinkLabel;
    newNode.questionDescription = this.questionDescription;
    newNode.optionalInputControlType = this.optionalInputControlType;
    newNode.optionalInputProperty = this.optionalInputProperty;
    newNode.optionalInputPropertyType = this.optionalInputPropertyType;
    newNode.optionalInputLabel = this.optionalInputLabel;
    newNode.modalType = this.modalType;
    newNode.obfuscateValueConditional = this.obfuscateValueConditional;
    newNode.readonlyConditional = this.readonlyConditional;
    newNode.readonlyReason = this.readonlyReason;
    if (this.section === 'Existing Policies' && !this.controller.customer.isCoverpath2019MetadataEnabled()) {
      newNode.sectionValidation = true;
    }
    newNode.dataReliesOn = this.dataReliesOn;
    newNode.conditionalDestroyBlock = this.conditionalDestroyBlock;
    newNode.tooltipDisabledOverride = this.tooltipDisabledOverride;
    newNode.customClientValidations = this.customClientValidations;
    newNode.resetLevel();

    return newNode;
  }

  isShown(loopIndex?: number) {
    if (this.controller.isTeleInterviewerLoggedIn && !this.isTeleCMIQuestionNode()) {
      return false;
    }

    let parentIsPresent: boolean = true;
    if (!this.parent) {
      /*
       * For 'normal' nodes, this signifies a top level node that is always shown,
       * however, if we have a loop index, this is a node within an iterate collection
       * that may have had it's parent severed, in that case, that node is _not_ shown
       */
      return isNaN(this.loopIndex);
    }

    if (
      this.parent.isIterateCollection() ||
      (this.parent.type && !this.parent.type.match(/boolean|string|number|multivalue|date/i))
    ) {
      parentIsPresent = this.parent.isShown(loopIndex);
    }

    // Fix for the Juvenile section due to family member iterate collection having no trigger
    // TODO HL-17949
    if (this.parent.type && this.parent.type.includes('PartyRelation')) {
      if (isNaN(loopIndex)) {
        return false;
      }
    }

    if (this.showifExp) {
      // eval in a nested scope
      try {
        return function () {
          return parentIsPresent && this.evaluateShowIfExpression(loopIndex);
        }.bind(this)();
      } catch (e) {}
    } else {
      return parentIsPresent;
    }
  }

  evaluateShowIfExpression(loopIndex?: number) {
    let showIfExp = this.showifExp;
    if (!isNaN(loopIndex)) {
      showIfExp = this.showifExp.replace(/\[i]/g, '[' + loopIndex + ']');
      /*since these are dynamic replacement of showif, have to add pubsub subscription here.
             takes parent question Prop to add subscribtion- which is what the child is interested in.
             ex: child question is interested in parent's prop ( customer.application.primaryPolicy.insured.familyHistory.familyConditions[0].detailedType */
      this.subscribeToProps.forEach((value) => {
        value = value.replace('[i]', '[' + loopIndex + ']');
        this.addToSubscriptionList(value);
      });
    } else {
      /*
        race conditions cause loopIndex to be undefined, yet showIfExp to still have [i] _very_ rarely
            as in, for the same question, out of thousands of iterations of isShown, only 1 will have this condition

        if iOS safari attempts to eval a show if with [i].something (undefined error), it _kills_ the page and does
            a hard refresh. this is atrocious user experience and would likely result in users dropping off

        specifically occurs with
            this.controller.customer.application.primaryPolicy.insured.riskFactors.ftCountries[i].name === "Mexico"

        I am aware that this is a monkey patch, but this error is very hard to track down, and must be prevented..

        replacing [i] with [0] prevents the undefined error for the one iteration where loopIndex is NaN
            any display errors will be corrected on the next iteration, within milliseconds
        */
      showIfExp = showIfExp.replace(/\[i]/g, '[0]');
    }

    //tslint:disable-next-line
    let result = eval(showIfExp);
    if (typeof result !== 'boolean' && showIfExp.indexOf('match') !== -1) {
      if (result && Array.isArray(result) && result[0] && (<any>result).input) {
        return true;
      } else {
        return false;
      }
      // return result.length > 0;
    } else {
      return result;
    }
  }

  addToSubscriptionList(property) {
    // No event defined, create custom subscription

    const propertyEventName = property + '_changed';
    if (this.subscriptionsInFlight.indexOf(propertyEventName) === -1) {
      const subject = SubjectManager.getSubject(propertyEventName);

      const subscription = subject.subscribe(() => {
        this.propertyChangedSubject.next();
      });

      if (subscription) {
        this.subscriptionsInFlight.push(propertyEventName); // insured_financials_occupation_changed format
      }

      SubjectManager.setSubject(propertyEventName, subject);
    }
  }

  setProp(prop) {
    this.prop = prop;
    this.fid = this.buildFid();
  }

  buildFid(prop?) {
    return (this.id + this.prop).replace(/[\.\[\]]/g, '_');
  }

  setupEventSubscriptions(parent?: CasMetadataNode) {
    // Look at all the explicit subscriptions from the meta data column
    // and add them to the subscription list
    this.interestedIn.forEach((propName) => {
      const propertyEventKey = propName.split('.').join('_');
      this.subscribeToProps.push(propertyEventKey);
    });

    if (this.prop) {
      // Subscribe to any changes to yourself
      let fullPropPath = [this.prop];
      if (this.customProps) {
        fullPropPath = [];
        this.customProps.forEach((value) => {
          fullPropPath.push(this.prop + '.' + value);
        });
      }
      fullPropPath.forEach((value) => {
        // This is a dynamic event name for reflexive questions
        const propertyEventKey = value.split('.').join('_');
        // Before we setup a subscription, check to see if this property already has an event
        const isSubscribeToProp = this.subscribeToProps.indexOf(propertyEventKey) !== -1;
        this.subscribeToProps.push(propertyEventKey);

        if (!isSubscribeToProp) {
          this.addToSubscriptionList(propertyEventKey);
        }
      });
    }

    // 1. Loop through any props in our showifExp to setup subscriptions
    // based on amorphic triggers
    // 2. Also add any subscriptions explicitly setup from metadata as there
    // are cases where a question neeeds to listen to changes outside showifExp questions.
    this.subscribeToProps.forEach((propName) => {
      this.addToSubscriptionList(propName);
    });
  }

  updateFid() {
    this.fid = this.buildFid();
  }

  buildResponsesAllowed(responsesAllowedStr: string): Array<ValueDescription> | null {
    // The input string is an array of key/value pairs
    if (responsesAllowedStr) {
      const logger = UILoggerSingleton.getInstance();
      try {
        // Replace single quotes as JSON.parse only accept double quotes
        let responsesAllowedObj;

        if (Utils.isJson(responsesAllowedStr)) {
          responsesAllowedObj = JSON.parse(responsesAllowedStr);
        } else {
          responsesAllowedObj = JSON.parse(responsesAllowedStr.replace(/\'/g, '"'));
        }

        if (Array.isArray(responsesAllowedObj)) {
          return responsesAllowedObj.map((item) => {
            const value = Object.keys(item)[0];
            let description = item[value];
            description = description.replace('&rsquo;', "'");

            return { value: value, description: description };
          });
        } else {
          logger.error(
            {
              module: 'MetadataNode',
              function: 'buildResponsesAllowed',
              category: UICategory.request,
              error: {
                isHumanRelated: false,
              },
            },
            `Metadata: buildResponsesAllowed Skipped - Not an array for ${this.id}`
          );
        }
      } catch (e) {
        const error = {
          isHumanRelated: false,
          name: e.name,
          message: e.message,
          stack: e.stack,
          data: {
            id: this.id,
          },
        };
        logger.error(
          {
            module: 'MetadataNode',
            function: 'buildResponsesAllowed',
            error: error,
            category: UICategory.request,
          },
          `Metadata: buildResponsesAllowed Skipped`
        );
      }
    }
    return null;
  }

  resetChildren(loopIndex?: number) {
    this.children.forEach((node) => {
      if (!node.conditionalDestroyBlock) {
        node.resetVal();
        if (!node.isIterateCollection()) {
          node.resetChildren(loopIndex);
        }
      }
    });
  }

  resetVal(loopIndex?: number) {
    // If this is a node with no prop or the prop does not exist, there is nothing to reset
    if (!this.isPropNonNull(loopIndex)) {
      return;
    }

    const customProps = this.customProps;
    let propName = this.prop;

    if (!isNaN(loopIndex)) {
      propName = propName.replace('[i]', '[' + loopIndex + ']');
    }
    let valueToSet = null;

    try {
      if (customProps) {
        customProps.forEach((prop) => {
          const customPropName = propName + '.' + prop;
          //tslint:disable-next-line
          eval(
            'controller.' +
              customPropName +
              ' = ' +
              valueToSet +
              '; if(typeof (controller.' +
              customPropName +
              'Trigger) === "function"){ controller.' +
              customPropName +
              'Trigger()}'
          );
        });
      } else {
        if (this.isIterateCollection()) {
          // Allow the trigger to do the reset
          return;
        } else if (this.isCheckbox()) {
          valueToSet = false;
        }

        // Eval used to take the property given, set it, and trigger an update if trigger exists
        //tslint:disable-next-line
        eval(
          'controller.' +
            propName +
            ' = ' +
            valueToSet +
            '; if(typeof (controller.' +
            propName +
            'Trigger) === "function"){ controller.' +
            propName +
            'Trigger()}'
        );
      }
    } catch (e) {
      // Log error?
    }
  }

  isPropNonNull(loopIndex?: number) {
    if (!this.prop) {
      return false;
    }

    // Split the propName into tokens and check for the existence of each token
    const tokens = this.prop.split('.');
    let entity = this.controller;
    return tokens.every((token) => {
      //replace entity[familyConditions[i]] -> entity[familyConditions][0]
      if (entity && token.indexOf('[i]') !== -1 && !isNaN(loopIndex)) {
        const propToReplace = token.replace('[i]', '');
        entity = entity[propToReplace][loopIndex];
        return true;
      }
      //for normal entity[familyHistory]
      else if (entity && typeof entity[token] !== 'undefined') {
        entity = entity[token];
        return true;
      } else {
        return false;
      }
    });
  }

  substitutePronouns(text) {
    if (this.pronounLocked) {
      return text;
    }
    return text;
  }

  findLevel1Node() {
    // Find the parent level 1 node
    if (this.level === 1) {
      return this;
    }

    let pNode = this.parent;
    while (pNode && pNode.level !== 1) {
      pNode = pNode.parent;
    }

    return pNode;
  }

  findPrevLevel1Node() {
    const level1Node = this.findLevel1Node();
    let i = _.indexOf(level1Node.parent.children, level1Node);

    if (i >= 0) {
      if (i === 0) {
        const topLevelNode = level1Node.parent;
        let j = _.indexOf(topLevelNode.parent.children, topLevelNode);

        if (j > 0) {
          return getLastNode(topLevelNode.parent.children[--j]);
        }
      } else {
        return level1Node.parent.children[--i];
      }
    }

    return null;

    function getLastNode(node) {
      return !node.topLevel ? node : node.children[node.children.length - 1];
    }
  }

  isValid(loopIndex?: number) {
    if (!this.isShown(loopIndex)) {
      return true;
    } else if (this.needsFixed) {
      return false;
    } else if (this.isIterateCollection()) {
      return this.validateIterate();
    } else if (!this.optional && (this.isCheckbox() || this.isCheckboxGroup())) {
      return this.validateCheckboxGroup(loopIndex);
    }

    if (this.validateValue(loopIndex)) {
      if (!this.children || this.children.every((item) => item.isValid())) {
        return true;
      }
    }
    return false;
  }

  isCheckboxGroup(): boolean {
    if (!this.children) {
      return false;
    }
    return this.children.some((child) => child instanceof NoneNode || child instanceof OtherNotSureNode);
  }

  isIterateCollection(): boolean {
    return !!(this.controlType && this.controlType.match(/iterate-collection/));
  }

  isCheckbox(): boolean {
    return !!(this.controlType && this.controlType.match(/^checkbox-basic/));
  }

  isJuviSignerExceptionNode(): boolean {
    return !!(this.modalType && this.modalType === 'signerEmail');
  }

  validateCheckboxGroup(loopIndex?: number): boolean {
    /*
     *  A checkbox group is valid if the node it sits on is valid and either of the below is true:
     *   1. It has no children
     *   2. All of its children are valid AND if there are child checkbox controls, at least 1 is selected
     */
    if (!this.validateValue(loopIndex)) {
      return false;
    }
    const childrenShown = this.children.filter((child) => child.isShown());
    if (!childrenShown || childrenShown.length === 0) {
      return true;
    } else {
      const childrenValid = childrenShown.every((child) => child.isValid(loopIndex));
      const checkboxChildren = this.children.filter((child) => child.isCheckbox() && child.isShown());
      if (checkboxChildren.length > 0) {
        const anyChildSelected = checkboxChildren.some((child) => child.hasValue(child.prop));
        return childrenValid && anyChildSelected;
      } else {
        return childrenValid;
      }
    }
  }

  validateJuviSignerExceptionNode(): boolean {
    const hasSigner = Utils.get(this.controller, 'customer.application.primaryPolicy.insured.hasSigner');
    const signerEmail = Utils.get(this.controller, 'customer.application.primaryPolicy.insured.signer.email');
    const insuredEmail = Utils.get(this.controller, 'customer.application.primaryPolicy.insured.person.email');
    return !(typeof hasSigner !== 'boolean' || (hasSigner === true && (!signerEmail || !insuredEmail)));
  }

  validateAcknowledgementsNode(): boolean {
    const customer = this.controller.customer;
    const policy = customer.application.primaryPolicy;
    const consentEnabled = this.controller.earlyInsuredConsentEnabled;
    const { insuredHasAcknowledged, ownerHasAcknowledged, otherOwnerHasAcknowledged } = customer.application;
    return !(
      (ValidationUtils.requiresSimpleInsuredAcknowledgement(policy, consentEnabled) && !insuredHasAcknowledged) ||
      (ValidationService.requiresOwnerAcknowledgement(policy) && !ownerHasAcknowledged) ||
      (ValidationService.requiresJointOwnerAcknowledgement(policy) && !otherOwnerHasAcknowledged)
    );
  }

  // Validate a given node, ignoring it's children
  validateValue(loopIndex?: number): boolean {
    if (this.controlType && this.controlType.match(/^label/)) {
      // Labels have no value
      return true;
    }

    // we cannot use the required_based_on + custom_control_properties
    // for these without changing the way customer app validates this control
    if (this.controlType && this.controlType.match(/^section-please-acknowledge/)) {
      return this.validateAcknowledgementsNode();
    } else if (this.controlType && this.controlType.match(/^section-verify-your-email/)) {
      return this.controller.customer.emailValidated;
    } else if (this.isCheckbox()) {
      return true;
    } else if (this.isJuviSignerExceptionNode() && !this.validateJuviSignerExceptionNode()) {
      // The 2019 filing defined the Signer's email outside of the metadata for juveniles.
      // Ideally once all in-flight 2019 apps have expired we should remove this exception
      return false;
    }

    const propName = this.prop;
    const customProps = this.customProps;
    const optionalProp = this.optionalInputProperty;

    if (propName && !this.optional) {
      if (customProps) {
        return customProps.every((prop, i) => {
          if (this.requiredBasedOn) {
            let condition = this.requiredBasedOn[i];
            if (!isNaN(loopIndex)) {
              condition = this.requiredBasedOn[i].replace('[i]', '[' + loopIndex + ']');
            }
            if (condition === 'no') {
              return true;
            }
            //tslint:disable-next-line
            else if (condition === 'yes' || eval('controller.customer.application.primaryPolicy.' + condition)) {
              const customPropName = propName + '.' + prop;
              return this.hasValue(customPropName, loopIndex);
            } else {
              return true;
            }
          }
          if (!this.requiredProps || this.requiredProps[i] === 'yes') {
            const customPropName = propName + '.' + prop;
            return this.hasValue(customPropName, loopIndex);
          } else {
            return true;
          }
        });
      }
      // radio group with text input
      else if (this.subProperty) {
        const logger = UILoggerSingleton.getInstance();
        let propValue;
        try {
          propValue = this.controller.resolveProp(propName, loopIndex);
        } catch (e) {
          const error = {
            isHumanRelated: false,
            name: e.name,
            message: e.message,
            stack: e.stack,
          };
          logger.error(
            {
              module: 'MetadataNode',
              function: 'validateValue',
              error: error,
              category: UICategory.request,
            },
            `Metadata: validateValue propName indicated but not found '${propName}'`
          );
        }
        const isRequired = propValue === this.subRequiredIf && propValue !== undefined;
        if (isRequired) {
          return this.hasValue(propName, loopIndex) && this.hasValue(this.subProperty, loopIndex);
        } else {
          return this.hasValue(propName, loopIndex);
        }
      } else if (optionalProp) {
        // for optional-input components, either the input field must have a value, or the "not applicable" checkbox must be true
        const optionalPropName = `${this.entity.replace('[i]', `[${loopIndex}]`)}.${optionalProp}`;
        let optionalPropValue = _.get(this.controller, optionalPropName);
        optionalPropValue = optionalPropValue === 'false' ? false : optionalPropValue; // false as a string should be false
        return this.hasValue(propName, loopIndex) || (this.hasValue(optionalPropName, loopIndex) && optionalPropValue);
      } else {
        return this.hasValue(propName, loopIndex);
      }
    }

    return true;
  }

  hasValue(propName, loopIndex?: number) {
    // false is a valid value for radiogroups.
    // 0 is a valid value for numbers
    let isValid: boolean;

    if (!isNaN(loopIndex)) {
      // Replace [i] with loopIndex for iterate controls
      propName = propName.replace('[i]', '[' + loopIndex + ']');
    }

    try {
      // Necessary eval
      //tslint:disable-next-line
      isValid =
        this.controlType.indexOf('radio-group') === 0 || this.controlType.indexOf('custom-waiver') === 0
          ? //tslint:disable-next-line
            eval(
              'controller.' + propName + ' != null && typeof controller.' + propName + " != 'undefined' ? true : false"
            )
          : //tslint:disable-next-line
            eval('controller.' + propName + ' === 0 || controller.' + propName + ' ? true : false');
    } catch (e) {
      // Log error??
      isValid = false;
    }

    return isValid;
  }

  validateIterate() {
    // For each element in the array, get the corresponding node
    // if the node is shown, make sure each element has a value
    const arrayProp = this.prop;
    const match = this.type.match(/array *of *([A-Za-z]*)/);
    const arrayType = RegExp.$1;

    // Eval node property passed by string
    //tslint:disable-next-line
    let arrayValues = eval('controller.' + arrayProp);

    if (arrayValues.length === 0) {
      return true;
    }

    return arrayValues.every((arrayVal, i) => {
      return this.children.every((item) => {
        return item.isValid(i);
      });
    });
  }

  getFullValidationString(prop: string): string {
    let metadataValidation: string = this.validation;
    const customProps: Array<string> = this.customProps;
    const customClientValidations: Array<string> = this.customClientValidations;

    if (prop && customProps && customClientValidations) {
      const propSplitArray: Array<string> = prop.split('.');
      const shortenedProp: string = propSplitArray[propSplitArray.length - 1];
      const indexOfCustomProp: number = customProps.indexOf(shortenedProp);
      if (indexOfCustomProp > -1 && customClientValidations.length > indexOfCustomProp) {
        // found match
        // use the validation from the same index in customClientValidations array
        const customValidationToRun: string = customClientValidations[indexOfCustomProp];
        if (customValidationToRun) {
          metadataValidation = metadataValidation
            ? metadataValidation + ';' + customValidationToRun
            : customValidationToRun;
        }
      } else {
        UILoggerSingleton.getInstance().error(
          {
            module: 'CasMetadataNode',
            function: 'getFullValidationString',
            category: UICategory.request,
            error: {
              isHumanRelated: false,
            },
          },
          `Metadata: getFullValidationString - incorrectly setup node ${this.id}. Check custom_control_properties and custom_client_validations, they should have the same length comma delimited lists.`
        );
      }
    }
    return metadataValidation;
  }

  /**
   * overrides other app logic for disabling question - a question marked with this conditional will always be disabled if the condition is met
   * will need to work in tandem with hardcoded values to ensure apps are not blocked from submission
   */
  getIsPermanentlyDisabledOnCondition(): boolean {
    const conditions: string[] = this.permanentlyDisabledConditional;
    const result: boolean = conditions.some((condition) => {
      // Regular expression to match a function name, such as abc(), or defg()
      const functionRegEx = /(.+)\(\)/;
      if (functionRegEx.test(condition)) {
        return this.controller.customer.application.primaryPolicy[condition.match(functionRegEx)[1]]();
      }
      switch (condition) {
        case 'shouldDisablePPAP':
          if (this.isShown()) {
            if (
              this.controller.customer.application.primaryPolicy.isPolicyPaymentFrequencyMonthly() ||
              this.controller.customer.application.primaryPolicy.isThirdPartyPayer()
            ) {
              return true;
            }
          }
          return false;
        case 'LifeBridge':
          return this.controller.customer.application.primaryPolicy.amorphicGetClassName() === 'LifeBridgePolicy';
        case 'isOwnerSameAsInsured':
          return this.controller.customer.application.primaryPolicy.isSelfInsuredAndSelfOwner();
        default: {
          return condition.toLocaleLowerCase() === 'true' ? true : false;
        }
      }
    });
    return result;
  }

  isTeleCMIQuestionNode(): boolean {
    if (this.isTeleCMIQuestion) {
      return true;
    }

    return this.parent ? this.parent.isTeleCMIQuestionNode() : false;
  }

  isOptionalDueToTeleCMI(): boolean {
    if (this.controller.customer.application.isTeleCMI && !this.controller.isTeleInterviewerLoggedIn) {
      if (this.parent && this.parent.isOptionalDueToTeleCMI()) {
        return true;
      }

      if (this.isTeleCMIQuestion) {
        return true;
      }
    }
    return false;
  }

  isOptionalDueToApplicationData(): boolean {
    const flags = {
      mmGiactWebDebitVerificationServiceEnabled: this.controller.mmGiactWebDebitVerificationServiceEnabled,
    };
    return ApplicationSectionUtils.shouldDisableSection(
      this.controller.customer.application.primaryPolicy,
      this.section,
      flags
    );
  }

  isQuestionLockableNode(): boolean {
    if (this.isQuestionLockable) {
      return true;
    }

    return this.parent ? this.parent.isQuestionLockableNode() : false;
  }
}

export class NoneNode extends CasMetadataNode {
  constructor(controller: CoverpathControllerInterface, prop?, options?) {
    super(controller, prop, options);
    this.setupEventSubscriptions(options.parent);
  }

  setupEventSubscriptions(parent) {
    //add subscribe to props for none node
    if (parent) {
      const children = parent.children;
      // If the node is "None of the above", uncheck siblings
      for (let i = 0; i < children.length; i++) {
        const child = parent.children[i];

        if (this === child) {
          break;
        } else {
          this.subscribeToProps.push(child.prop.split('.').join('_')); // subscribe to every checkbox in that group except on its own
        }
      }
      //add subscription
      this.subscribeToProps.forEach((value) => {
        this.addToSubscriptionList(value);
      });
    }
  }
}

export class OtherNotSureNode extends CasMetadataNode {}

export class DiagnosisShortNode extends CasMetadataNode {
  isShown(loopIndex?: number): boolean {
    const isVisible = super.isShown();

    // DiagnosisShort type properties have no triggers as they are controlled
    // by metadata. Instantiate them when they are supposed to be showing and are
    // null.
    this.runTrigger(isVisible);
    return isVisible;
  }

  protected runTrigger(isVisible: boolean) {
    const tokens = this.prop.split('.');
    const propName = tokens.pop(); // Remove the last element

    // construct the entity
    let entity = this.controller;
    tokens.forEach(function (token) {
      entity = entity[token];
    });
    if (!entity) {
      return;
    }

    if (isVisible) {
      if (entity[propName] === null || typeof entity[propName] === 'undefined') {
        if (this.controller.applicationController) {
          entity[propName] = this.controller.applicationController.createIterateClass(
            'DiagnosisShort',
            this.controller.customer
          );
        }
      }
    } else {
      entity[propName] = null;
    }
  }
}

export class DiagnosisNode extends DiagnosisShortNode {
  runTrigger(isVisible: boolean) {
    const tokens = this.prop.split('.');
    const propName = tokens.pop(); // Remove the last element

    // construct the entity
    let entity = this.controller;
    tokens.forEach(function (token) {
      entity = entity[token];
    });

    if (isVisible) {
      if (entity[propName] === null) {
        entity[propName] = this.controller.applicationController.createIterateClass(
          'Diagnosis',
          this.controller.customer
        );
      }
    } else {
      entity[propName] = null;
    }
  }
}
